import { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Loader2, CreditCard, CheckCircle, Download, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from './ui/progress';
import UpiPaymentPage from './UpiPaymentPage';

const RSVPModal = ({ event, goal, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [step, setStep] = useState('rsvp'); // 'rsvp' or 'payment'
  const [loading, setLoading] = useState(false);
  const [rsvpId, setRsvpId] = useState(null);
  const [eventPlanInfo, setEventPlanInfo] = useState(null);
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [formData, setFormData] = useState({
    rsvp_status: 'yes',
    message: '',
    payment_method: 'upi',
    adults_male: 0,
    adults_female: 0,
    kids_male: 0,
    kids_female: 0
  });

  // Track contributions for each goal: {goal_id: amount}
  const [goalContributions, setGoalContributions] = useState({});
  
  // Track "Others" custom contribution
  const [showOthersInput, setShowOthersInput] = useState(false);
  const [othersContribution, setOthersContribution] = useState({
    title: '',
    description: '',
    amount: ''
  });
  
  // Track manual UPI ID input
  const [manualUpiId, setManualUpiId] = useState('');
  
  // Payment page modal state - SINGLE PAYMENT TO EVENT CREATOR ONLY
  const [showPaymentPage, setShowPaymentPage] = useState(false);
  const [selectedUpiId, setSelectedUpiId] = useState('');
  
  const totalGuests = formData.adults_male + formData.adults_female + formData.kids_male + formData.kids_female;
  
  // Calculate total contribution amount (including Others) - NO COMMISSION
  const totalContribution = Object.values(goalContributions).reduce((sum, amount) => {
    return sum + (parseFloat(amount) || 0);
  }, 0) + (parseFloat(othersContribution.amount) || 0);

  // Fetch real-time budget status on mount
  useEffect(() => {
    fetchBudgetStatus();
  }, []);

  const fetchBudgetStatus = async () => {
    try {
      const response = await axios.get(`${API}/events/${event.id}/budget-status`);
      setBudgetStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch budget status:', error);
    }
  };

  // Determine event plan based on total budget
  const getEventPlan = () => {
    const totalEventBudget = event.contribution_goals.reduce((sum, goal) => sum + goal.target_amount, 0);
    
    if (totalEventBudget <= 20000) {
      return { name: "Basic Plan", max_budget: 20000 };
    } else if (totalEventBudget <= 100000) {
      return { name: "Standard Plan", max_budget: 100000 };
    } else if (totalEventBudget <= 500000) {
      return { name: "Premium Plan", max_budget: 500000 };
    } else {
      return { name: "Enterprise Plan", max_budget: 1000000 };
    }
  };

  const eventPlan = budgetStatus?.plan || getEventPlan();

  const handleRSVP = async () => {
    setLoading(true);
    try {
      // Build contributions array and validate minimum amounts
      const contributions = [];
      let hasMinimumError = false;
      
      Object.entries(goalContributions).forEach(([goalId, amount]) => {
        const parsedAmount = parseFloat(amount);
        if (parsedAmount > 0) {
          // Find the goal to check minimum contribution
          const goal = event.contribution_goals.find(g => g.id === goalId);
          
          if (goal && goal.minimum_contribution && parsedAmount < goal.minimum_contribution) {
            hasMinimumError = true;
            toast.error(`Contribution for "${goal.title}" must be at least ₹${goal.minimum_contribution}`);
          } else {
            contributions.push({
              goal_id: goalId,
              amount: parsedAmount
            });
          }
        }
      });
      
      // Add "Others" contribution if provided
      if (showOthersInput && parseFloat(othersContribution.amount) > 0) {
        if (!othersContribution.title.trim()) {
          toast.error('Please provide a title for your custom contribution');
          setLoading(false);
          return;
        }
        
        // Validate custom contribution doesn't exceed remaining budget
        const customAmount = parseFloat(othersContribution.amount);
        const remainingBudget = budgetStatus?.remaining_budget || eventPlan.max_budget;
        
        if (customAmount > remainingBudget) {
          toast.error(`Custom contribution cannot exceed remaining budget of ₹${remainingBudget.toLocaleString()}. (Total collected so far: ₹${budgetStatus?.total_collected?.toLocaleString() || 0})`);
          setLoading(false);
          return;
        }
        
        contributions.push({
          goal_id: "other",
          amount: parseFloat(othersContribution.amount),
          custom_goal_title: othersContribution.title,
          custom_goal_description: othersContribution.description
        });
      }

      // Stop if there are minimum amount errors
      if (hasMinimumError) {
        setLoading(false);
        return;
      }

      // Validate guest count for "Yes" and "Maybe" RSVP status
      if (formData.rsvp_status === 'yes' || formData.rsvp_status === 'maybe') {
        const totalGuestCount = formData.adults_male + formData.adults_female + formData.kids_male + formData.kids_female;
        if (totalGuestCount === 0) {
          toast.error('Please enter the number of guests coming. At least one guest must be specified.');
          setLoading(false);
          return;
        }
      }

      const payload = {
        event_id: event.id,
        rsvp_status: formData.rsvp_status,
        message: formData.message || undefined,
        adults_male: parseInt(formData.adults_male) || 0,
        adults_female: parseInt(formData.adults_female) || 0,
        kids_male: parseInt(formData.kids_male) || 0,
        kids_female: parseInt(formData.kids_female) || 0,
        contributions: contributions
      };

      const response = await axios.post(
        `${API}/rsvps`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data) {
        setRsvpId(response.data.id);
        
        // If contribution was made, show single payment to event creator
        if (totalContribution > 0) {
          toast.success(`RSVP created! Please pay ₹${totalContribution.toLocaleString()} to event creator`);
          setStep('payment');
        } else {
          toast.success('RSVP recorded successfully!');
          onSuccess();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create RSVP');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPhoto = (photoUrl, index) => {
    try {
      const link = document.createElement('a');
      link.href = photoUrl;
      link.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}_photo_${index + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Photo download started');
    } catch (error) {
      toast.error('Failed to download photo');
    }
  };

  const generateUpiLink = (upiId, appPrefix = 'upi') => {
    const amount = totalContribution;
    const payeeName = event.host_name || 'Event Host';
    const transactionNote = `Contribution for ${event.title}`;
    
    // URL encode parameters
    const params = new URLSearchParams({
      pa: upiId,
      pn: payeeName,
      am: amount.toString(),
      cu: 'INR',
      tn: transactionNote
    });
    
    // Generate app-specific deep link
    const deepLink = `${appPrefix}://pay?${params.toString()}`;
    return deepLink;
  };

  const handleOpenPaymentPage = (upiId) => {
    setSelectedUpiId(upiId);
    setShowPaymentPage(true);
  };

  const handleManualUpiPayment = () => {
    if (!manualUpiId || !manualUpiId.includes('@')) {
      toast.error('Please enter a valid UPI ID');
      return;
    }
    handleOpenPaymentPage(manualUpiId);
  };

  const handlePaymentPageComplete = async (transactionId, transactionDate) => {
    setShowPaymentPage(false);
    
    // Single payment to event creator - NO COMMISSION
    try {
      await axios.post(
        `${API}/rsvps/${rsvpId}/upload-payment-proof`,
        { 
          transaction_id: transactionId,
          transaction_date: transactionDate
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      toast.success('Payment details submitted!', {
        description: 'Event creator will verify your contribution.',
        duration: 5000
      });
      
      onSuccess();
    } catch (error) {
      toast.error('Failed to save payment details');
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      // Update payment status to success (user confirms they paid)
      await axios.put(
        `${API}/rsvps/${rsvpId}/payment-status`,
        { status: 'success' },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          params: { status: 'success' }
        }
      );

      toast.success('✅ Payment confirmed! Thank you for your contribution.');
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rsvp-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {step === 'rsvp' ? 'RSVP & Contribute' : 'Complete Payment'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {step === 'rsvp' ? (
            <>
              {/* Event Photos */}
              {event.event_photos && event.event_photos.length > 0 && (
                <div className="border-2 border-contribo-gold rounded-lg p-4 bg-gradient-to-r from-contribo-navy from-opacity-5 to-contribo-gold to-opacity-5">
                  <Label className="text-base font-semibold text-contribo-navy mb-3 block">
                    📸 Event Photos
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {event.event_photos.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={photo}
                          alt={`Event photo ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border-2 border-contribo-navy shadow-md"
                        />
                        <button
                          onClick={() => handleDownloadPhoto(photo, index)}
                          className="absolute bottom-1 right-1 bg-contribo-navy text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-contribo-gold"
                          title="Download photo"
                          type="button"
                        >
                          <Download className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <Label>Will you be attending? *</Label>
                <RadioGroup
                  value={formData.rsvp_status}
                  onValueChange={(value) => setFormData({ ...formData, rsvp_status: value })}
                  data-testid="rsvp-status-selector"
                >
                  <div className="flex items-center space-x-2 border-2 border-green-100 p-3 rounded-lg bg-green-50">
                    <RadioGroupItem value="yes" id="yes" data-testid="rsvp-yes-option" />
                    <Label htmlFor="yes" className="flex-1 cursor-pointer">
                      <span className="font-medium text-green-700">✓ Yes, Coming</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 border-2 border-red-100 p-3 rounded-lg bg-red-50">
                    <RadioGroupItem value="no" id="no" data-testid="rsvp-no-option" />
                    <Label htmlFor="no" className="flex-1 cursor-pointer">
                      <span className="font-medium text-red-700">✗ No, Not coming</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 border-2 border-yellow-100 p-3 rounded-lg bg-yellow-50">
                    <RadioGroupItem value="maybe" id="maybe" data-testid="rsvp-maybe-option" />
                    <Label htmlFor="maybe" className="flex-1 cursor-pointer">
                      <span className="font-medium text-yellow-700">? Maybe</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Guest Count Section */}
              <div className="space-y-3 border-2 border-purple-100 p-4 rounded-lg bg-contribo-navy bg-opacity-50">
                <Label className="text-base font-semibold">Number of Guests Coming</Label>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="adults_male" className="text-sm">
                      Adults - Male
                      <span className="text-xs text-gray-500 block">(Above 12 years)</span>
                    </Label>
                    <Input
                      id="adults_male"
                      type="number"
                      min="0"
                      value={formData.adults_male}
                      onChange={(e) => setFormData({ ...formData, adults_male: parseInt(e.target.value) || 0 })}
                      data-testid="adults-male-input"
                      disabled={formData.rsvp_status === 'no'}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="adults_female" className="text-sm">
                      Adults - Female
                      <span className="text-xs text-gray-500 block">(Above 12 years)</span>
                    </Label>
                    <Input
                      id="adults_female"
                      type="number"
                      min="0"
                      value={formData.adults_female}
                      onChange={(e) => setFormData({ ...formData, adults_female: parseInt(e.target.value) || 0 })}
                      data-testid="adults-female-input"
                      disabled={formData.rsvp_status === 'no'}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="kids_male" className="text-sm">
                      Kids - Male
                      <span className="text-xs text-gray-500 block">(Under 12 years)</span>
                    </Label>
                    <Input
                      id="kids_male"
                      type="number"
                      min="0"
                      value={formData.kids_male}
                      onChange={(e) => setFormData({ ...formData, kids_male: parseInt(e.target.value) || 0 })}
                      data-testid="kids-male-input"
                      disabled={formData.rsvp_status === 'no'}
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="kids_female" className="text-sm">
                      Kids - Female
                      <span className="text-xs text-gray-500 block">(Under 12 years)</span>
                    </Label>
                    <Input
                      id="kids_female"
                      type="number"
                      min="0"
                      value={formData.kids_female}
                      onChange={(e) => setFormData({ ...formData, kids_female: parseInt(e.target.value) || 0 })}
                      data-testid="kids-female-input"
                      disabled={formData.rsvp_status === 'no'}
                    />
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg border border-purple-200">
                  <p className="text-sm text-gray-600">Total Guests:</p>
                  <p className="text-2xl font-bold text-contribo-navy" data-testid="total-guests-display">
                    {totalGuests}
                  </p>
                </div>
              </div>

              {/* Contribution Goals Section */}
              <div className="space-y-3 border-2 border-contribo-navy p-4 rounded-lg bg-white">
                <Label className="text-base font-semibold text-contribo-navy">
                  Contribute to Goals (Optional)
                </Label>
                <p className="text-sm text-gray-700 font-medium">
                  Enter amounts for multiple goals if you wish
                </p>
                
                {/* Check if fundraising is paused */}
                {event.is_fundraising_paused ? (
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 text-center">
                    <p className="text-yellow-800 font-semibold">
                      Funds are not accepting at the moment
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      The host has paused contributions for this event
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Goals List with Amount Inputs */}
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {event.contribution_goals.map((eventGoal) => {
                        const progress = (eventGoal.current_amount / eventGoal.target_amount) * 100;
                        const hasAmount = goalContributions[eventGoal.id] && parseFloat(goalContributions[eventGoal.id]) > 0;
                        
                        return (
                          <div
                            key={eventGoal.id}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              hasAmount
                                ? 'border-contribo-navy bg-contribo-navy bg-opacity-100'
                                : 'border-gray-200 bg-white'
                            } ${eventGoal.is_completed ? 'opacity-50' : ''}`}
                            data-testid={`goal-option-${eventGoal.id}`}
                          >
                            <div className="space-y-3">
                              <div>
                                <h4 className={`font-semibold flex items-center justify-between ${
                                  hasAmount ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {eventGoal.title}
                                  {eventGoal.is_completed && (
                                    <span className="text-xs font-semibold text-green-400">✓ Completed</span>
                                  )}
                                </h4>
                                {eventGoal.description && (
                                  <p className={`text-xs mt-1 ${
                                    hasAmount ? 'text-gray-200' : 'text-gray-600'
                                  }`}>{eventGoal.description}</p>
                                )}
                                {eventGoal.additional_info && (
                                  <div className={`mt-2 p-2 rounded-md ${
                                    hasAmount ? 'bg-contribo-gold bg-opacity-20' : 'bg-contribo-gold bg-opacity-10'
                                  }`}>
                                    <p className={`text-xs font-semibold mb-1 ${
                                      hasAmount ? 'text-contribo-gold' : 'text-contribo-navy'
                                    }`}>Additional Info:</p>
                                    {eventGoal.additional_info.startsWith('http') ? (
                                      <a 
                                        href={eventGoal.additional_info} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className={`text-xs hover:underline break-all ${
                                          hasAmount ? 'text-contribo-gold' : 'text-contribo-gold'
                                        }`}
                                      >
                                        {eventGoal.additional_info}
                                      </a>
                                    ) : (
                                      <p className={`text-xs ${
                                        hasAmount ? 'text-gray-200' : 'text-gray-700'
                                      }`}>{eventGoal.additional_info}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              <div className="space-y-1">
                                <div className={`flex justify-between text-xs ${
                                  hasAmount ? 'text-gray-200' : 'text-gray-600'
                                }`}>
                                  <span>Raised: ₹{eventGoal.current_amount.toLocaleString()}</span>
                                  <span>Goal: ₹{eventGoal.target_amount.toLocaleString()}</span>
                                </div>
                                <Progress value={progress} className="h-2" />
                                <p className={`text-xs font-semibold ${
                                  hasAmount ? 'text-contribo-gold' : 'text-contribo-navy'
                                }`}>
                                  {progress.toFixed(1)}% Complete
                                </p>
                              </div>

                              <div>
                                <Label htmlFor={`amount-${eventGoal.id}`} className={`text-sm ${
                                  hasAmount ? 'text-white' : 'text-gray-900'
                                }`}>
                                  Your Contribution (₹)
                                  {eventGoal.minimum_contribution && (
                                    <span className={`ml-2 text-xs ${
                                      hasAmount ? 'text-contribo-gold' : 'text-contribo-navy'
                                    }`}>
                                      (Min: ₹{eventGoal.minimum_contribution})
                                    </span>
                                  )}
                                </Label>
                                <Input
                                  id={`amount-${eventGoal.id}`}
                                  type="number"
                                  placeholder={eventGoal.minimum_contribution ? `Min ₹${eventGoal.minimum_contribution}` : "Enter amount"}
                                  value={goalContributions[eventGoal.id] || ''}
                                  onChange={(e) => setGoalContributions({
                                    ...goalContributions,
                                    [eventGoal.id]: e.target.value
                                  })}
                                  min={eventGoal.minimum_contribution || 0}
                                  className={hasAmount ? 'bg-white text-gray-900' : ''}
                                  data-testid={`contribution-input-${eventGoal.id}`}
                                />
                                {eventGoal.minimum_contribution && hasAmount && parseFloat(goalContributions[eventGoal.id]) < eventGoal.minimum_contribution && (
                                  <p className="text-xs mt-1 text-red-400 font-semibold">
                                    ⚠ Minimum contribution is ₹{eventGoal.minimum_contribution}
                                  </p>
                                )}
                                {eventGoal.is_completed && (
                                  <p className={`text-xs mt-1 ${
                                    hasAmount ? 'text-green-300' : 'text-green-600'
                                  }`}>
                                    ✓ Goal reached! Additional contributions welcome
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Others Custom Contribution */}
                    {event.contribution_goals.length < 5 && (
                      <div className="border-2 border-dashed border-contribo-gold rounded-lg p-3">
                        {!showOthersInput ? (
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full border-contribo-gold text-contribo-navy hover:bg-contribo-gold hover:bg-opacity-10"
                            onClick={() => setShowOthersInput(true)}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Custom Contribution (Others)
                          </Button>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <Label className="text-sm font-semibold text-contribo-navy">
                              Custom Contribution
                            </Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShowOthersInput(false);
                                setOthersContribution({ title: '', description: '', amount: '' });
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
                            ℹ️ As per the <strong>{eventPlan.name}</strong> selected by the event creator, 
                            the total budget for this event should be under <strong>₹{eventPlan.max_budget.toLocaleString()}</strong>.
                            {budgetStatus && (
                              <div className="mt-1 font-semibold">
                                💰 Collected so far: ₹{budgetStatus.total_collected.toLocaleString()} | 
                                Available: ₹{budgetStatus.remaining_budget.toLocaleString()}
                              </div>
                            )}
                          </div>
                          
                          <div>
                            <Label htmlFor="others-title" className="text-xs">
                              Title *
                            </Label>
                            <Input
                              id="others-title"
                              placeholder="e.g., Gift, Personal Expense"
                              value={othersContribution.title}
                              onChange={(e) => setOthersContribution({ ...othersContribution, title: e.target.value })}
                              className="text-sm"
                            />
                          </div>
                          
                          <div>
                            <Label htmlFor="others-description" className="text-xs">
                              Description (Optional)
                            </Label>
                            <Textarea
                              id="others-description"
                              placeholder="Add details about your contribution..."
                              value={othersContribution.description}
                              onChange={(e) => setOthersContribution({ ...othersContribution, description: e.target.value })}
                              rows={2}
                              className="text-sm"
                            />
                          </div>
                          
                          <div>
                            <Label htmlFor="others-amount" className="text-xs">
                              Amount (₹) *
                            </Label>
                            <Input
                              id="others-amount"
                              type="number"
                              placeholder="Enter amount"
                              value={othersContribution.amount}
                              onChange={(e) => setOthersContribution({ ...othersContribution, amount: e.target.value })}
                              min="0"
                              max={budgetStatus?.remaining_budget || eventPlan.max_budget}
                              className="text-sm"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Maximum you can contribute: ₹{(budgetStatus?.remaining_budget || eventPlan.max_budget).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      )}
                      </div>
                    )}

                    {/* Total Contribution */}
                    {totalContribution > 0 && (
                      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                        <p className="text-sm text-gray-600">Total Contribution:</p>
                        <p className="text-2xl font-bold text-green-600" data-testid="total-contribution">
                          ₹{totalContribution.toLocaleString()}
                        </p>
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-600 italic">
                      You can contribute to multiple goals at once or add a custom contribution
                    </p>
                  </>
                )}
              </div>

              {/* UPI IDs Section */}
              {event.upi_ids && event.upi_ids.length > 0 && (
                <div className="space-y-3 border-2 border-contribo-gold p-4 rounded-lg bg-contribo-gold bg-opacity-10">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-contribo-gold bg-opacity-20 rounded-full flex items-center justify-center">
                      <span className="text-contribo-navy font-bold">₹</span>
                    </div>
                    <Label className="text-base font-semibold text-contribo-navy">
                      Payment Details
                    </Label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Send your contribution to the host's UPI ID:
                  </p>
                  <div className="space-y-2">
                    {event.upi_ids.map((upiId, index) => (
                      <div 
                        key={index} 
                        className="bg-white border-2 border-contribo-navy rounded-lg p-3 flex items-center justify-between"
                      >
                        <span className="font-mono text-contribo-navy font-semibold">
                          {upiId}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(upiId);
                            toast.success('UPI ID copied to clipboard!');
                          }}
                          className="text-xs bg-contribo-gold text-white px-3 py-1 rounded-full hover:bg-contribo-gold-dark"
                        >
                          Copy
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 italic">
                    💡 After making the payment, please complete your RSVP below
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="message" className="text-base font-semibold text-contribo-navy">
                  ✨ Blessings / Additional Message
                </Label>
                <Textarea
                  id="message"
                  placeholder="Share your blessings, wishes, or message for the host..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="border-contribo-gold border-2"
                  data-testid="rsvp-message-input"
                />
                <p className="text-xs text-gray-500 italic">
                  Express your heartfelt wishes and blessings (optional)
                </p>
              </div>

              <Button
                className="w-full bg-gradient-to-r from-contribo-navy to-contribo-gold text-white"
                onClick={handleRSVP}
                disabled={loading}
                data-testid="rsvp-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating RSVP...
                  </>
                ) : (
                  formData.contribution_amount && parseFloat(formData.contribution_amount) > 0
                    ? 'Continue to Payment'
                    : 'Submit RSVP'
                )}
              </Button>
            </>
          ) : (
            <>
              {/* Payment Step - SINGLE PAYMENT TO EVENT CREATOR */}
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg border-2 border-green-500">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Pay Contribution to Event Creator</p>
                  <p className="text-xs text-gray-600 mb-3">
                    100% of your contribution goes directly to the event creator - No commission!
                  </p>
                  <p className="text-3xl font-bold text-green-600">
                    ₹{totalContribution.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* UPI Payment Section */}
              {event.upi_ids && event.upi_ids.length > 0 ? (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-green-200 rounded-lg p-4">
                    <Label className="text-base font-semibold text-gray-900 mb-3 block">
                      <CreditCard className="inline h-5 w-5 mr-2" />
                      Pay via UPI Apps
                    </Label>
                    <p className="text-sm text-gray-700 mb-4">
                      Click on any UPI app to pay ₹{totalContribution.toLocaleString()} directly
                    </p>
                    
                    {/* Host's UPI IDs with App Buttons */}
                    {event.upi_ids.map((upiId, index) => (
                      <div key={index} className="mb-4 last:mb-0">
                        <div className="bg-white p-3 rounded-lg border-2 border-gray-200 mb-2">
                          <Label className="text-xs text-gray-600">Host's UPI ID:</Label>
                          <div className="flex items-center justify-between">
                            <p className="font-mono font-bold text-sm text-gray-900">{upiId}</p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(upiId);
                                toast.success('UPI ID copied!');
                              }}
                              className="text-xs"
                            >
                              Copy
                            </Button>
                          </div>
                        </div>

                        {/* Pay Now Button */}
                        <Button
                          className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white py-4"
                          onClick={() => handleOpenPaymentPage(upiId)}
                        >
                          <CreditCard className="mr-2 h-5 w-5" />
                          Pay ₹{totalContribution.toLocaleString()} Now
                        </Button>
                      </div>
                    ))}

                    {/* Manual UPI ID Input */}
                    <div className="mt-4 pt-4 border-t-2 border-gray-200">
                      <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                        Or enter UPI ID manually:
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="yourname@upi"
                          value={manualUpiId}
                          onChange={(e) => setManualUpiId(e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          variant="outline"
                          onClick={handleManualUpiPayment}
                          className="border-2 border-green-500 text-green-600 hover:bg-green-50"
                        >
                          Pay Now
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        This will open your default UPI app
                      </p>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-800">
                      <strong>💡 Tip:</strong> Click "Pay Now" button to open the payment page. You'll see QR code or UPI app options to complete payment.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <p className="text-xs text-yellow-800">
                      <strong>MVP Note:</strong> Mock payment integration. In production, you'll be redirected to the actual payment gateway.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <Label>Select Payment Method</Label>
                    <RadioGroup
                      value={formData.payment_method}
                      onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                    >
                      <div className="flex items-center space-x-2 border-2 border-purple-100 p-3 rounded-lg">
                        <RadioGroupItem value="razorpay" id="razorpay" />
                        <Label htmlFor="razorpay" className="flex-1 cursor-pointer">
                          <div className="flex items-center">
                            <CreditCard className="h-5 w-5 mr-2 text-blue-600" />
                            <span className="font-medium">Razorpay (UPI, Cards, etc.)</span>
                          </div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2 border-2 border-purple-100 p-3 rounded-lg">
                        <RadioGroupItem value="paypal" id="paypal" />
                        <Label htmlFor="paypal" className="flex-1 cursor-pointer">
                          <div className="flex items-center">
                            <CreditCard className="h-5 w-5 mr-2 text-blue-800" />
                            <span className="font-medium">PayPal</span>
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <Button
                    className="w-full bg-gradient-to-r from-contribo-navy to-contribo-gold text-white"
                    onClick={handlePayment}
                    disabled={loading}
                    data-testid="payment-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      `Pay ₹{totalContribution.toLocaleString()}`
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>

      {/* UPI Payment Page Modal - SINGLE PAYMENT TO EVENT CREATOR */}
      {showPaymentPage && (
        <UpiPaymentPage
          isOpen={showPaymentPage}
          onClose={() => setShowPaymentPage(false)}
          amount={totalContribution}
          upiId={selectedUpiId}
          payeeName={event.host_name || 'Event Host'}
          transactionNote={`Contribution for ${event.title}`}
          onPaymentComplete={handlePaymentPageComplete}
          title="Pay Contribution to Event Creator"
        />
      )}
    </Dialog>
  );
};

export default RSVPModal;
